import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Image, Col, Row } from "react-bootstrap";
import { useTranslateDynamicFields } from "../../api/queries/useTranslateDynamicFields";
import { User } from "../../types/Types";
import "../css/ProfileModal.css";
import { MdOutlineBusiness } from "react-icons/md";
import {
  FaBuilding,
  FaBullseye,
  FaGlobe,
  FaGraduationCap,
  FaIdBadge,
  FaIndustry,
  FaInfoCircle,
  FaLink,
  FaMapMarkerAlt,
  FaTools,
  FaUniversity,
  FaUserCheck,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";
import CurvedTextOverlay from "../Account/CurvedTextOverlay";
import { useMediaQuery } from "react-responsive";
import {
  getMembershipLabel,
  getProfileMembershipBadgeTheme,
  normalizeMembership,
} from "../../utils/connectionUserUtils";

interface ProfileModalProps {
  show: boolean;
  onHide: () => void;
  selectedUser: User | null;
  /** Falls back to translated default when omitted */
  title?: string;
  isConnected?: boolean;
}

const IMAGE_SIZES = { desktop: 150, lgTablet: 140, mdTablet: 130, mobile: 95 } as const;

const formatName = (name: string) => {
  return name ? name.charAt(0).toUpperCase() + name.slice(1) : "";
};

const OBJECT_FIT_COVER = { objectFit: "cover" as const };

function ProfileModal({
  show,
  onHide,
  selectedUser,
  title,
  isConnected,
}: ProfileModalProps) {
  const { t, i18n } = useTranslation();
  const displayTitle = title ?? t("profile.defaultTitle");
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const isLgTablet = useMediaQuery({ minWidth: 992, maxWidth: 1023 });
  const isMdTablet = useMediaQuery({ minWidth: 768, maxWidth: 991 });

  const imageSize = useMemo(
    () =>
      isDesktop
        ? IMAGE_SIZES.desktop
        : isLgTablet
          ? IMAGE_SIZES.lgTablet
          : isMdTablet
            ? IMAGE_SIZES.mdTablet
            : IMAGE_SIZES.mobile,
    [isDesktop, isLgTablet, isMdTablet]
  );

  const fullName = useMemo(() => {
    if (!selectedUser) return "";
    return `${formatName(selectedUser.firstName || "")} ${formatName(selectedUser.lastName || "")}`.trim();
  }, [selectedUser]);

  const membershipInfo = useMemo(() => {
    if (!selectedUser) return "";
    if (normalizeMembership(selectedUser.membershipName) === "Youth Membership") {
      return selectedUser.studyInterest
        ? formatName(selectedUser.studyInterest)
        : "";
    }
    return selectedUser.personalPositionCompany
      ? formatName(selectedUser.personalPositionCompany)
      : "";
  }, [selectedUser]);

  /** Circled summary block only — translated when UI language is not English */
  const profileSummaryFields = useMemo(() => {
    if (!selectedUser) return {};
    const fields: Record<string, string> = {};
    if (fullName) fields.fullName = fullName;
    if (membershipInfo) fields.membershipInfo = membershipInfo;
    const sector = selectedUser.businessSectorIndustry?.trim();
    if (sector) fields.sector = sector;
    const city = selectedUser.city?.trim();
    if (city) fields.city = city;
    const businessName = selectedUser.businessName?.trim();
    if (businessName) fields.businessName = businessName;
    const universityCollege = selectedUser.universityCollege?.trim();
    if (universityCollege) fields.universityCollege = universityCollege;
    return fields;
  }, [selectedUser, fullName, membershipInfo]);

  const { data: translatedSummary } = useTranslateDynamicFields(
    profileSummaryFields,
    i18n.resolvedLanguage || i18n.language
  );

  const displayFullName =
    translatedSummary?.fullName?.trim() || fullName;
  const displayMembershipInfo =
    translatedSummary?.membershipInfo?.trim() || membershipInfo;
  const displaySector =
    translatedSummary?.sector?.trim() ||
    selectedUser?.businessSectorIndustry ||
    "";
  const displayCity =
    translatedSummary?.city?.trim() || selectedUser?.city || "";
  const displayBusinessName =
    translatedSummary?.businessName?.trim() || selectedUser?.businessName || "";
  const displayUniversityCollege =
    translatedSummary?.universityCollege?.trim() ||
    selectedUser?.universityCollege ||
    "";

  const profileImageUrl = useMemo(
    () =>
      selectedUser?.ProfessionalPhoto ||
      selectedUser?.PersonalProfessionalPhoto ||
      "/user.png",
    [selectedUser?.ProfessionalPhoto, selectedUser?.PersonalProfessionalPhoto]
  );

  const membershipBadgeTheme = useMemo(
    () =>
      getProfileMembershipBadgeTheme({
        membershipName: selectedUser?.membershipName ?? "",
        explorerAttendeeType: "",
      }),
    [selectedUser?.membershipName]
  );

  const bannerStyle = useMemo(
    () => ({
      backgroundImage: selectedUser?.BannerImage
        ? `url(${selectedUser.BannerImage})`
        : undefined,
      backgroundSize: "cover" as const,
      backgroundPosition: "center" as const,
      backgroundRepeat: "no-repeat" as const,
      width: "100%",
      minHeight: "150px",
      aspectRatio: "3 / 1",
    }),
    [selectedUser?.BannerImage]
  );

  const imageContainerStyle = useMemo(
    () => ({ width: imageSize, height: imageSize }),
    [imageSize]
  );

  const isYouthMembership =
    !!selectedUser &&
    normalizeMembership(selectedUser.membershipName) === "Youth Membership";

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      centered
      className="profile-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>{displayTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {selectedUser && (
          <>
            <div className="card mx-auto rounded-3 overflow-hidden border-0 shadow-lg">
              <div
                className={`${
                  selectedUser.BannerImage ? "" : "gradient-bg"
                } position-relative profile-banner d-flex justify-content-center align-items-center`}
                style={bannerStyle}
              >
                {!selectedUser.BannerImage && (
                  <div className="d-flex h-100 w-100 justify-content-center align-items-center">
                    <img
                      src="/SMBF-logo-white.png"
                      alt={t("profile.alt.smbfLogo")}
                      width={120}
                      height={120}
                      className="w-25 h-25"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                )}
                <div className="position-absolute translate-middle image-container">
                  <div
                    className="position-relative w-100 h-100"
                    style={imageContainerStyle}
                  >
                    <Image
                      src={profileImageUrl}
                      roundedCircle
                      alt={t("profile.alt.user")}
                      width={imageSize}
                      height={imageSize}
                      className="profile-image w-100 h-100 shadow-sm border border-2 border-white p-1"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        const t = e.target as HTMLImageElement;
                        if (t.src && !t.src.endsWith("/user.png")) t.src = "/user.png";
                      }}
                    />

                    {membershipBadgeTheme && (
                      <span className="badge-overlay text-white fw-bold px-2 py-1 rounded-pill">
                        <CurvedTextOverlay
                          size={imageSize}
                          stroke={membershipBadgeTheme.stroke}
                          text={membershipBadgeTheme.text}
                        />
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="mx-3 pt-5 pb-2 flex-grow-1">
                <div className="d-flex justify-content-between align-items-center profile-summary-row">
                  <div className="mt-md-4 mt-lg-5 profile-summary-primary">
                    <h2 className="mb-0">{displayFullName}</h2>
                    {displayMembershipInfo && <span>{displayMembershipInfo}</span>}
                    {displaySector && (
                      <div className="d-flex gap-1">
                        <span className="text-muted">{displaySector}</span>
                      </div>
                    )}
                    {!isConnected && displayCity && (
                      <div className="d-flex gap-1 text-muted align-items-center">
                        <FaMapMarkerAlt /> <span>{displayCity}</span>
                      </div>
                    )}
                  </div>
                  <div className="d-none d-md-block profile-summary-secondary">
                    {isYouthMembership ? (
                      <div className="fw-semibold d-flex gap-1 align-items-center">
                        <FaUniversity size={25} />
                        {displayUniversityCollege || t("profile.notProvidedYet")}
                      </div>
                    ) : (
                      <div className="d-flex gap-3 align-items-center cursor-pointer">
                        {selectedUser.businessLogo ? (
                          <img
                            src={selectedUser.BusinessLogo}
                            className="rounded-circle"
                            width={40}
                            height={40}
                            alt={
                              selectedUser.businessName
                                ? t("profile.alt.businessLogoFor", {
                                    name: selectedUser.businessName,
                                  })
                                : t("profile.alt.businessLogo")
                            }
                            loading="lazy"
                            decoding="async"
                            style={OBJECT_FIT_COVER}
                          />
                        ) : (
                          <MdOutlineBusiness size={30} />
                        )}
                        <div className="fw-semibold">
                          {displayBusinessName || t("profile.notProvidedYet")}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="">
                {isYouthMembership ? (
                  isConnected ? (
                    <div className="px-3">
                      <hr />
                      <Row>
                        <Col md={4} className="mb-3 d-block d-md-none">
                          <div className="d-flex align-items-start">
                            <FaGraduationCap
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>{t("profile.label.educationLevel")}</strong>
                              <div className="text-muted small">
                                {selectedUser.educationLevel ||
                                  t("profile.notProvidedYet")}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaIdBadge
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>{t("profile.label.membershipTypeLower")}</strong>
                              <div className="text-muted small">
                                {getMembershipLabel(selectedUser.membershipName ?? "") ||
                                  "Not Provided Yet"}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaTools
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>{t("profile.label.skillsExperience")}</strong>
                              <div className="text-muted small">
                                {selectedUser.skillsExperience ||
                                  t("profile.notProvidedYet")}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col md={4} className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaUsers
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>{t("profile.label.peerNetworking")}</strong>
                              <div className="text-muted small">
                                {selectedUser.peerNetworking ||
                                  t("profile.notProvidedYet")}
                              </div>
                            </div>
                          </div>
                        </Col>
                        <Col className="mb-3">
                          <div className="d-flex align-items-start">
                            <FaUserCircle
                              size={20}
                              className="me-2 mt-1 text-secondary"
                            />
                            <div>
                              <strong>{t("profile.label.about")}</strong>
                              <div className="text-muted small">
                                {selectedUser.aboutYou || t("profile.notProvidedYet")}
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </div>
                    ) : (
                      <div className="p-3 d-block d-md-none">
                        <hr />
                        <h5 className="fw-semibold mb-3">{t("profile.section.educationDetails")}</h5>
                        <Row>
                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaGraduationCap
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.educationLevel")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.universityCollege ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )
                ) : (
                  <>
                    {isConnected ? (
                      <div className="p-3">
                        <hr className="mt-0" />
                        <h5 className="fw-semibold mb-3">{t("profile.section.businessDetails")}</h5>
                        <Row>
                          <Col md={4} className="mb-3 d-block d-md-none">
                            <div className="d-flex align-items-start">
                              <FaBuilding
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div className="d-flex justify-content-between w-100">
                                <div>
                                  <strong>{t("profile.label.businessName")}</strong>
                                  <div className="text-muted small">
                                    {selectedUser.businessName ||
                                      t("profile.notProvidedYet")}
                                  </div>
                                </div>
                                {selectedUser.BusinessLogo && (
                                  <img
                                    src={selectedUser.BusinessLogo}
                                    className="rounded-circle me-2 mt-1"
                                    width={40}
                                    height={40}
                                    alt={
                              selectedUser.businessName
                                ? t("profile.alt.businessLogoFor", {
                                    name: selectedUser.businessName,
                                  })
                                : t("profile.alt.businessLogo")
                            }
                                    loading="lazy"
                                    decoding="async"
                                    style={OBJECT_FIT_COVER}
                                  />
                                )}
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIndustry
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.sectorIndustry")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessSectorIndustry ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaGlobe
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.countriesOperation")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessCountryOperation ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaInfoCircle
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.aboutBusiness")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessAbout ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaLink
                                size={20}
                                className="me-2 mt-1 text-secondary flex-shrink-0"
                              />
                              <div className="d-flex flex-column w-100">
                                <strong>{t("profile.label.websiteUrl")}</strong>
                                <a
                                  href={selectedUser.businessWebsiteUrl || "#"}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted small text-truncate pr-1"
                                >
                                  {selectedUser.businessWebsiteUrl ||
                                    t("profile.notProvidedYet")}
                                </a>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaBullseye
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.objectives")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessObjectives ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>

                          <Col md={4} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIdBadge
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />

                              <div>
                                <strong>{t("profile.label.membershipTypeCap")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.membershipName ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                        <hr />
                        <h5 className="fw-semibold mb-3">{t("profile.section.discounts")}</h5>
                        <Row>
                          <Col md={6} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaIdBadge
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>Products or Services Offered</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessProductServices ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>
                          <Col md={6} className="mb-3">
                            <div className="d-flex align-items-start">
                              <FaUserCheck
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div>
                                <strong>{t("profile.label.discountsPlatform")}</strong>
                                <div className="text-muted small">
                                  {selectedUser.businessDiscount ||
                                    t("profile.notProvidedYet")}
                                </div>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    ) : (
                      <div className="p-3 d-block d-md-none">
                        <hr />
                        <h5 className="fw-semibold mb-3">{t("profile.section.businessDetails")}</h5>
                        <Row>
                          <Col md={4} className="mb-3 ">
                            <div className="d-flex align-items-start">
                              <FaBuilding
                                size={20}
                                className="me-2 mt-1 text-secondary"
                              />
                              <div className="d-flex justify-content-between w-100">
                                <div>
                                  <strong>{t("profile.label.businessName")}</strong>
                                  <div className="text-muted small">
                                    {selectedUser.businessName ||
                                      t("profile.notProvidedYet")}
                                  </div>
                                </div>
                                <img
                                  src={selectedUser.BusinessLogo}
                                  className="rounded-circle me-2 mt-1"
                                  width={40}
                                  height={40}
                                  alt={
                              selectedUser.businessName
                                ? t("profile.alt.businessLogoFor", {
                                    name: selectedUser.businessName,
                                  })
                                : t("profile.alt.businessLogo")
                            }
                                  loading="lazy"
                                  decoding="async"
                                  style={OBJECT_FIT_COVER}
                                />
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

ProfileModal.displayName = "ProfileModal";

export default memo(ProfileModal);
